import { ColumnFilterValues, GameDataRefDto } from './game-data.types';

/**
 * Пары синтеза — вторая таблица игровых данных (после стилей персонажа).
 * DTO повторяют контракт `/admin/game-data/synthesis` один в один.
 *
 * Отличие от стилей: **категорий и матрицы применимых полей здесь нет** — все поля есть
 * у каждой строки, прятать по признаку категории нечего.
 */

/**
 * Код режима синтеза — весь список значений, допустимых в игровых данных.
 *
 * Подпись СТРОКИ приходит с бэка (`automaticTypeTitle`) на языке запроса — её и показываем
 * в таблице. Подписи для выпадающих списков лежат в `game-data.json`
 * (`synthesis.automaticType.*`): списком значений бэк не отдаёт, взять их с сервера неоткуда.
 */
export type SynthesisAutomaticType = 'NONE' | 'REPEAT' | 'GROWNUP' | 'LEVELUP' | 'MAX';

/** Порядок для выпадающих списков: фильтр, ячейка, форма, массовая правка. */
export const SYNTHESIS_AUTOMATIC_TYPES: SynthesisAutomaticType[] = [
  'NONE',
  'REPEAT',
  'GROWNUP',
  'LEVELUP',
  'MAX',
];

/**
 * Поля, которые массовая правка умеет очищать.
 * **Грабли:** значение, которого бэк не знает, роняет весь `/bulk`, а не одно поле —
 * список сверять с контрактом при каждом изменении.
 */
export type SynthesisClearField = 'PITY_STEP';

export type SynthesisSortOption =
  | 'id'
  | 'id_desc'
  | 'chance'
  | 'chance_desc'
  | 'group'
  | 'group_desc'
  | 'updated'
  | 'updated_desc';

/**
 * Пара синтеза: два предмета в слотах → результат, с шансом, гарантией и наградами за неудачу.
 *
 * Поля `itemFirstSlot` / `itemSecondSlot` / `resultItem` / `brokeItem` / `additionFailItem` —
 * обогащение НА ЧТЕНИЕ (`GameDataRefDto`): в запросах уходят голые id. `name === null` значит,
 * что предмета нет в вики — показываем сам id, это сигнал, что данные разъехались.
 */
export interface SynthesisDto {
  id: number;
  /** Версия строки. Её обязательно вернуть в PUT, иначе правка отклоняется как устаревшая. */
  version: number;

  itemIdFirstSlot: number;
  /** Расшифровка `itemIdFirstSlot`. Только на чтение. */
  itemFirstSlot?: GameDataRefDto | null;
  itemCountFirstSlot?: number | null;

  itemIdSecondSlot: number;
  /** Расшифровка `itemIdSecondSlot`. Только на чтение. */
  itemSecondSlot?: GameDataRefDto | null;
  itemCountSecondSlot?: number | null;

  resultItemId: number;
  /** Расшифровка `resultItemId`. Только на чтение. */
  resultItem?: GameDataRefDto | null;

  commission: number;
  /** Шанс успеха в процентах, 0.01–100. Клиент понимает только целые — см. подсказку на форме. */
  chance: number;
  /** На сколько процентов растёт гарантия после каждой неудачи. Пусто — гарантии у пары нет. */
  pityStep?: number | null;

  brokeId?: number | null;
  brokeCount?: number | null;
  /** Расшифровка `brokeId`. Только на чтение. */
  brokeItem?: GameDataRefDto | null;

  additionFailId?: number | null;
  additionFailCount?: number | null;
  /** Расшифровка `additionFailId`. Только на чтение. */
  additionFailItem?: GameDataRefDto | null;

  groupId: number;
  automaticType: SynthesisAutomaticType;
  /** Подпись режима на языке запроса — в таблице показываем её, а не свою. */
  automaticTypeTitle: string;
  /** Уровень и максимальный уровень осмысленны только при `automaticType = LEVELUP`. */
  level: number;
  maxLevel: number;

  createdAt: string;
  updatedAt: string;
  updatedById?: number | null;
  updatedByUsername?: string | null;
}

/**
 * Тело POST `/` — создание пары.
 * Обязательны предметы обоих слотов, результат и шанс; у остальных полей есть дефолты на бэке
 * (`commission` = 0, `groupId` = 1, `automaticType` = GROWNUP, `level` = 1, `maxLevel` = 1).
 */
export interface CreateSynthesisDto {
  itemIdFirstSlot: number;
  itemCountFirstSlot?: number;
  itemIdSecondSlot: number;
  itemCountSecondSlot?: number;
  resultItemId: number;
  commission?: number;
  chance: number;
  pityStep?: number;
  brokeId?: number;
  brokeCount?: number;
  additionFailId?: number;
  additionFailCount?: number;
  groupId?: number;
  automaticType?: SynthesisAutomaticType;
  level?: number;
  maxLevel?: number;
}

/**
 * Тело PUT `/{id}` — ПОЛНАЯ замена: не переданные необязательные поля очищаются.
 * `version` обязателен, без него правка отклоняется.
 */
export interface UpdateSynthesisDto extends CreateSynthesisDto {
  version: number;
  commission: number;
  groupId: number;
  automaticType: SynthesisAutomaticType;
  level: number;
  maxLevel: number;
}

/** Строка для массовой правки: что меняем и какую версию читали. */
export interface SynthesisRowVersionDto {
  id: number;
  version: number;
}

/**
 * Тело POST `/bulk`. Применяются ТОЛЬКО переданные поля.
 * Предметы, результат и награды массово не меняются — их в диалоге нет.
 */
export interface BulkUpdateSynthesisDto {
  rows: SynthesisRowVersionDto[];
  commission?: number;
  chance?: number;
  pityStep?: number;
  groupId?: number;
  automaticType?: SynthesisAutomaticType;
  /** Только парой с `maxLevel`. */
  level?: number;
  maxLevel?: number;
  /** Поля, которые нужно очистить у всех выбранных пар. */
  clear?: SynthesisClearField[];
}

export interface BulkUpdateSynthesisResultDto {
  /** Пары, к которым изменения применены. */
  updated: SynthesisDto[];
  /** Изменённые кем-то другим за это время: НЕ применены, здесь их актуальные значения. */
  conflicted: SynthesisDto[];
}

export interface BulkDeleteSynthesisDto {
  ids: number[];
}

export interface PaginatedSynthesisDto {
  content: SynthesisDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

/** Параметры GET `/`. */
/**
 * Фильтры по колонкам списка пар — точное совпадение. Параметра `itemId` (предмет в любом слоте)
 * у бэка больше нет: предмет ищется в своей колонке.
 */
export const SYNTHESIS_FILTER_PARAMS = [
  'id',
  'itemIdFirstSlot',
  'itemCountFirstSlot',
  'itemIdSecondSlot',
  'itemCountSecondSlot',
  'resultItemId',
  'commission',
  'chance',
  'pityStep',
  'brokeId',
  'brokeCount',
  'additionFailId',
  'additionFailCount',
  'groupId',
  'automaticType',
  'level',
  'maxLevel',
] as const;

export interface SynthesisFilters {
  /** Фильтры по колонкам (`SYNTHESIS_FILTER_PARAMS`). */
  columns?: ColumnFilterValues;
  sort?: SynthesisSortOption;
  page?: number;
  size?: number;
}

/** Ограничения бэка — держим рядом, чтобы валидация формы не расходилась с ним. */
export const SYNTHESIS_LIMITS = {
  /** id предметов и количества — не меньше 1. */
  ID_MIN: 1,
  COUNT_MIN: 1,
  /** Клиентская группа синтеза: с 1, по умолчанию 1. */
  GROUP_MIN: 1,
  COMMISSION_MIN: 0,
  CHANCE_MIN: 0.01,
  CHANCE_MAX: 100,
  PITY_MIN: 1,
  PITY_MAX: 100,
  LEVEL_MIN: 1,
  /** `brokeCount` и `additionFailCount` — short на бэке. */
  SHORT_MAX: 32767,
  /** `rows` в `/bulk` и `ids` в `/bulk/delete`. */
  BULK_MAX_ROWS: 1000,
} as const;
